import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Radio from "@mui/material/Radio";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import { alpha } from "@mui/material/styles";
import CheckRounded from "@mui/icons-material/CheckRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import ErrorOutlineRounded from "@mui/icons-material/ErrorOutlineRounded";
import ImageOutlined from "@mui/icons-material/ImageOutlined";
import { useUnifiedStore } from "../../store/StoreContext";
import { useTranslation } from "../../hooks";
export function PaymentMethodPicker({ showOnline, paymentTimeoutMinutes, }) {
    const { t } = useTranslation("appointment");
    const methods = useUnifiedStore((s) => s.availablePaymentMethods);
    const selected = useUnifiedStore((s) => s.selectedPaymentMethod);
    const setPaymentMethod = useUnifiedStore((s) => s.setPaymentMethod);
    if (!showOnline && methods.length === 0)
        return null;
    const holdMinutes = typeof paymentTimeoutMinutes === "number" && paymentTimeoutMinutes > 0
        ? paymentTimeoutMinutes
        : null;
    return (_jsxs(Box, { sx: { mt: 2 }, children: [_jsx(Typography, { variant: "subtitle2", sx: { fontWeight: 600, mb: 1, px: 0.25 }, children: t("howWouldYouLikeToPay", "How would you like to pay?") }), _jsxs(Box, { role: "radiogroup", "aria-label": t("howWouldYouLikeToPay", "How would you like to pay?"), sx: {
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1.5,
                    overflow: "hidden",
                }, children: [_jsx(Option, { selected: selected.kind === "AT_CLINIC", onSelect: () => setPaymentMethod({ kind: "AT_CLINIC" }), title: t("payAtClinic", "Pay at the clinic"), subtitle: t("payAtClinicHint", "Settle it at the counter when you arrive.") }), showOnline && (_jsx(Option, { selected: selected.kind === "ONLINE", onSelect: () => setPaymentMethod({ kind: "ONLINE" }), title: t("payOnline", "Pay online"), subtitle: t("payOnlineHint", "Card, UPI or net banking, through our payment partner."), children: holdMinutes !== null && (_jsx(Alert, { severity: "info", sx: { mt: 1, py: 0.5 }, children: t("slotHoldWarning", "Your slot is held for {{minutes}} minutes while you pay. If the payment is not completed by then, the slot opens back up and we refund any charge automatically.", { minutes: holdMinutes }) })) })), methods.map((method) => (_jsx(Option, { selected: selected.kind === "MANUAL" && selected.methodId === method.id, onSelect: () => setPaymentMethod({ kind: "MANUAL", methodId: method.id }), title: method.name, subtitle: method.kind === "QR"
                            ? t("scanToPay", "Scan the clinic's QR code to pay.")
                            : t("payByUpi", "Pay to the clinic's UPI ID."), children: _jsx(ManualMethodDetails, { method: method }) }, method.id)))] })] }));
}
function Option({ selected, onSelect, title, subtitle, children, }) {
    return (_jsxs(Box, { sx: {
            borderTop: 1,
            borderColor: "divider",
            "&:first-of-type": { borderTop: 0 },
            bgcolor: selected ? "action.hover" : "transparent",
        }, children: [_jsxs(Box, { role: "radio", "aria-checked": selected, tabIndex: 0, onClick: onSelect, onKeyDown: (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onSelect();
                    }
                }, sx: {
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    p: 1.25,
                    cursor: "pointer",
                }, children: [_jsx(Radio, { checked: selected, size: "small", sx: { p: 0.25, mt: 0.125 } }), _jsxs(Box, { sx: { minWidth: 0 }, children: [_jsx(Typography, { variant: "body2", sx: { fontWeight: 600 }, children: title }), subtitle && (_jsx(Typography, { variant: "caption", color: "text.secondary", children: subtitle }))] })] }), selected && children && (_jsx(Box, { sx: { px: 1.25, pb: 1.5, pl: 4.5 }, children: children }))] }));
}
const EYEBROW_SX = {
    display: "block",
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: "0.09em",
    textTransform: "uppercase",
    color: "text.secondary",
};
const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace";
const CALM_MOTION = {
    transition: "background-color 150ms ease-out, border-color 150ms ease-out, color 150ms ease-out",
    "@media (prefers-reduced-motion: reduce)": { transition: "none" },
};
const focusRing = (theme) => ({
    "&:focus-visible": {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: 2,
    },
});
function ManualMethodDetails({ method }) {
    const { t } = useTranslation("appointment");
    const [copied, setCopied] = React.useState(false);
    const copy = async () => {
        if (!method.paymentLink)
            return;
        try {
            await navigator.clipboard.writeText(method.paymentLink);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
        catch {
        }
    };
    return (_jsxs(Box, { children: [_jsx(Typography, { component: "span", sx: EYEBROW_SX, children: method.kind === "QR"
                    ? t("stepScanAndPay", "1 · Scan and pay")
                    : t("stepPay", "1 · Pay") }), method.kind === "QR" && method.qrImageUrl && (_jsx(Box, { sx: (theme) => ({
                    mt: 1,
                    width: "fit-content",
                    p: 1,
                    borderRadius: 1.5,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: "#fff",
                }), children: _jsx(Box, { component: "img", src: method.qrImageUrl, alt: t("qrCodeAlt", "Payment QR code"), sx: {
                        width: 168,
                        height: 168,
                        objectFit: "contain",
                        display: "block",
                    } }) })), method.kind === "UPI" && method.paymentLink && (_jsxs(Box, { sx: (theme) => ({
                    mt: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    pl: 1.25,
                    pr: 0.5,
                    py: 0.5,
                    borderRadius: 1.5,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: alpha(theme.palette.primary.main, 0.035),
                }), children: [_jsx(Typography, { component: "code", sx: {
                            flex: 1,
                            minWidth: 0,
                            fontFamily: MONO,
                            fontSize: 13.5,
                            fontWeight: 500,
                            lineHeight: 1.5,
                            wordBreak: "break-all",
                        }, children: method.paymentLink }), _jsx(Button, { size: "small", onClick: copy, "aria-label": t("copyUpiId", "Copy UPI ID"), startIcon: copied ? (_jsx(CheckRounded, { sx: { fontSize: 15 } })) : (_jsx(ContentCopyRounded, { sx: { fontSize: 15 } })), sx: (theme) => ({
                            flexShrink: 0,
                            minWidth: 0,
                            px: 1,
                            fontSize: 12,
                            fontWeight: 600,
                            color: copied ? theme.palette.success.main : undefined,
                            ...CALM_MOTION,
                            ...focusRing(theme),
                        }), children: _jsx(Box, { component: "span", "aria-live": "polite", children: copied ? t("copied", "Copied") : t("copy", "Copy") }) })] })), method.instructions && (_jsx(Typography, { variant: "caption", sx: { display: "block", mt: 1, color: "text.secondary" }, children: method.instructions })), _jsx(Divider, { sx: { my: 1.75 } }), _jsx(ClaimDraftFields, {}), _jsxs(Box, { sx: { display: "flex", gap: 0.75, mt: 1.75 }, children: [_jsx(ErrorOutlineRounded, { sx: {
                            fontSize: 14,
                            mt: "2px",
                            color: "text.secondary",
                            flexShrink: 0,
                        } }), _jsx(Typography, { variant: "caption", sx: { color: "text.secondary", lineHeight: 1.5 }, children: t("manualPaymentNotice", "This payment happens outside MedOS — your appointment stays unpaid until the clinic verifies it.") })] })] }));
}
function ClaimDraftFields() {
    const { t } = useTranslation("appointment");
    const reference = useUnifiedStore((s) => s.claimDraftReference);
    const proof = useUnifiedStore((s) => s.claimDraftProof);
    const proofError = useUnifiedStore((s) => s.claimDraftProofError);
    const setReference = useUnifiedStore((s) => s.setClaimDraftReference);
    const setProof = useUnifiedStore((s) => s.setClaimDraftProof);
    const statusId = React.useId();
    const hasEvidence = reference.trim().length > 0 || proof !== null;
    return (_jsxs(Box, { children: [_jsx(Typography, { component: "span", sx: EYEBROW_SX, children: t("stepConfirmPayment", "2 · Confirm it") }), _jsx(Typography, { variant: "body2", sx: { fontWeight: 600, mt: 0.75 }, children: t("shareYourPaymentDetails", "Share proof of your payment") }), _jsx(Typography, { variant: "caption", sx: { display: "block", mt: 0.25, color: "text.secondary" }, children: t("shareYourPaymentDetailsHint", "The clinic matches this against their account. Either one is enough.") }), _jsx(TextField, { size: "small", fullWidth: true, sx: { mt: 1.25 }, label: t("transactionReference", "Transaction reference"), placeholder: t("transactionReferencePlaceholder", "UTR / transaction id"), value: reference, onChange: (e) => setReference(e.target.value), slotProps: { htmlInput: { "aria-describedby": statusId } }, helperText: t("referenceHelp", "The UTR or transaction id from your banking app.") }), _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1, my: 1 }, children: [_jsx(Box, { sx: { flex: 1, height: "1px", bgcolor: "divider" } }), _jsx(Typography, { variant: "caption", sx: { fontSize: 11, color: "text.tertiary" }, children: t("or", "or") }), _jsx(Box, { sx: { flex: 1, height: "1px", bgcolor: "divider" } })] }), proof ? (_jsxs(Box, { sx: (theme) => ({
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minHeight: 44,
                    pl: 1.25,
                    pr: 0.5,
                    py: 0.5,
                    borderRadius: 1.5,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: alpha(theme.palette.success.main, 0.06),
                }), children: [_jsx(ImageOutlined, { sx: { fontSize: 17, color: "text.secondary", flexShrink: 0 } }), _jsx(Typography, { variant: "caption", sx: { flex: 1, minWidth: 0, wordBreak: "break-all" }, children: proof.name }), _jsxs(Button, { component: "label", size: "small", sx: (theme) => ({
                            flexShrink: 0,
                            minWidth: 0,
                            px: 1,
                            fontSize: 12,
                            fontWeight: 600,
                            ...focusRing(theme),
                        }), children: [t("replaceScreenshot", "Replace"), _jsx("input", { type: "file", hidden: true, accept: "image/png,image/jpeg,image/webp", onChange: (e) => setProof(e.target.files?.[0] ?? null) })] }), _jsx(IconButton, { size: "small", onClick: () => setProof(null), "aria-label": t("removeScreenshot", "Remove screenshot"), sx: (theme) => ({ flexShrink: 0, ...focusRing(theme) }), children: _jsx(CloseRounded, { sx: { fontSize: 16 } }) })] })) : (_jsxs(Button, { component: "label", fullWidth: true, startIcon: _jsx(ImageOutlined, { sx: { fontSize: 17 } }), sx: (theme) => ({
                    minHeight: 44,
                    justifyContent: "center",
                    borderRadius: 1.5,
                    border: `1px dashed ${theme.palette.divider}`,
                    color: "text.secondary",
                    fontSize: 13,
                    fontWeight: 500,
                    textTransform: "none",
                    ...CALM_MOTION,
                    "&:hover": {
                        border: `1px dashed ${theme.palette.primary.main}`,
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                        color: theme.palette.text.primary,
                    },
                    "&:focus-within": {
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: 2,
                    },
                }), children: [t("attachScreenshot", "Attach a screenshot"), _jsx("input", { type: "file", hidden: true, accept: "image/png,image/jpeg,image/webp", onChange: (e) => setProof(e.target.files?.[0] ?? null) })] })), proofError && (_jsx(Typography, { role: "alert", variant: "caption", sx: { display: "block", mt: 0.75, color: "error.main" }, children: proofError })), _jsx(Typography, { id: statusId, role: "status", variant: "caption", sx: {
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                    color: hasEvidence ? "success.main" : "text.secondary",
                    ...CALM_MOTION,
                }, children: hasEvidence ? (_jsxs(_Fragment, { children: [_jsx(CheckRounded, { sx: { fontSize: 14, mt: "2px", flexShrink: 0 } }), t("evidenceCaptured", "We'll pass this to the clinic to verify.")] })) : (t("evidenceRequired", "Enter a transaction reference or attach a screenshot to continue — the clinic needs one of them to verify your payment.")) })] }));
}
